import {Controller, useFormContext} from 'react-hook-form';
import {
  CountrySelect,
  CreateTournamentForm,
  FormNames,
} from 'packages/common-types/base-types';
import {BASE_COUNTRY_URL, INPUT_FIELDS} from 'packages/constants/constants';
import Select from 'react-select';
import useFetch from 'packages/hooks/use-fetch';
import {Address} from 'packages/common-types/schema-types';
import {useEffect, useState} from 'react';

type Props = {
  address?: Address;
};

/**
 * Piece of the form with the address information of the user
 * @returns Component with the Address part of the form
 */
const FormAddress = ({address}: Props) => {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const {data: countries} = useFetch({apiUrl: `${BASE_COUNTRY_URL}countries`});
  const {get: getCountryStates} = useFetch({skip: true});
  const {get: getCitiesOfState} = useFetch({skip: true});

  const [loadedCountry, setLoadedCountry] = useState(null);
  const [loadedState, setLoadedState] = useState(null);
  const [loadedCity, setLoadedCity] = useState(null);

  const {control} = useFormContext<CreateTournamentForm>();

  useEffect(() => {
    if (address) {
      const {country, state, city} = address;
      setLoadedCountry({
        id: country,
        name: country,
      });
      setLoadedState({
        id: state,
        name: state,
      });
      setLoadedCity({
        id: city,
        name: city,
      });
    }
  }, [address]);

  const selectStyles = {
    menu: (provided: any) => ({
      ...provided,
      zIndex: 200,
    }),
  };

  return (
    <>
      <Controller
        control={control}
        name={INPUT_FIELDS[FormNames.COUNTRY].name}
        render={({onChange, ref, value, ...rest}) => (
          <div style={{marginBottom: 4, marginTop: 8}}>
            <Select
              inputRef={ref}
              styles={selectStyles}
              id="country-select"
              classNamePrefix="address-select"
              placeholder="Selecciona el país"
              onChange={async (data: CountrySelect) => {
                if (data) {
                  onChange(data.name);
                  setSelectedCountry(data.iso2);
                  const response = await getCountryStates(
                    `${BASE_COUNTRY_URL}countries/${data.iso2}/states`
                  );
                  setStates(response);
                  setLoadedCountry(null);
                } else {
                  setSelectedCountry(``);
                  setStates([]);
                  setCities([]);
                }
              }}
              getOptionValue={(item: CountrySelect) => item.name}
              getOptionLabel={(item) => `${item.name}` || ''}
              options={countries}
              value={
                loadedCountry ||
                countries?.find((c: CountrySelect) => c.name === value)
              }
              {...rest}
            />
          </div>
        )}
      />

      {/* State */}

      <Controller
        control={control}
        name={INPUT_FIELDS[FormNames.STATE].name}
        render={({onChange, ref, value, ...rest}) => (
          <div style={{marginBottom: 4, marginTop: 8}}>
            <Select
              inputRef={ref}
              styles={selectStyles}
              id="state-select"
              classNamePrefix="address-select"
              placeholder="Selecciona el departamento"
              onChange={async (newValue) => {
                if (newValue) {
                  onChange(newValue.name);
                  const response = await getCitiesOfState(
                    `${BASE_COUNTRY_URL}countries/${selectedCountry}/states/${newValue.iso2}/cities`
                  );
                  setCities(response);
                  setLoadedState(null);
                }
              }}
              getOptionValue={(item: CountrySelect) => item.name}
              getOptionLabel={(item) => `${item.name}` || ''}
              options={states}
              value={
                loadedState ||
                states?.find((c: CountrySelect) => c.name === value) ||
                []
              }
              {...rest}
            />
          </div>
        )}
      />

      {/* City */}
      <Controller
        rules={{
          required: false,
        }}
        control={control}
        name={INPUT_FIELDS[FormNames.CITY].name}
        render={({onChange, ref, value, ...rest}) => (
          <div style={{marginBottom: 4, marginTop: 8}}>
            <Select
              inputRef={ref}
              styles={selectStyles}
              id="city-select"
              classNamePrefix="address-select"
              placeholder="Selecciona la ciudad"
              onChange={(newValue: CountrySelect) => {
                onChange(newValue.name);
                setLoadedCity(null);
              }}
              getOptionValue={(item: CountrySelect) => item.name}
              getOptionLabel={(item: CountrySelect) => `${item.name}` || ''}
              options={cities}
              value={
                loadedCity ||
                cities?.find((c: CountrySelect) => c.name === value) ||
                []
              }
              {...rest}
            />
          </div>
        )}
      />
    </>
  );
};

FormAddress.defaultProps = {
  address: null,
};

export default FormAddress;
